/**
 * CacheService — cache em memória + sessionStorage com deduplicação de requests.
 *
 * Camadas:
 *  1. memoryCache     → Map em RAM, acesso O(1), sem serialização
 *  2. sessionStorage  → persiste hot-reload de dev, limpo automaticamente no F5
 *  3. inflightCache   → deduplicação: N chamadas simultâneas para a mesma chave
 *                       disparam apenas 1 request real
 *
 * Sentinel CACHE_NULL:
 *  null é um valor legítimo de negócio (config ainda não salva pelo usuário).
 *  Para distinguir "não está no cache" de "está no cache e vale null",
 *  usamos um símbolo interno como sentinela. Externamente, null continua
 *  sendo retornado normalmente — o sentinel nunca vaza para fora da classe.
 */

const CACHE_NULL = Symbol("CACHE_NULL");

interface CacheEntry<T> {
  // value pode ser T ou o sentinel interno
  value: T | typeof CACHE_NULL;
  expiresAt: number | null;
}

class CacheService {
  private static memoryCache = new Map<string, CacheEntry<any>>();
  private static inflightCache = new Map<string, Promise<any>>();

  // ─── API principal ────────────────────────────────────────────────────────

  /**
   * Busca um valor, usando cache quando disponível.
   * Cacheia inclusive respostas null (config inexistente no servidor).
   *
   * @param key      Chave do cache
   * @param fetcher  Função assíncrona que busca o dado na API
   * @param ttlMs    Tempo de vida em ms (omita para sem expiração)
   */
  static async fetch<T>(
    key: string,
    fetcher: () => Promise<T | null>,
    ttlMs?: number
  ): Promise<T | null> {
    // 1. Cache hit — inclui entradas com valor null
    const cached = this.getRaw(key);
    if (cached !== CACHE_NULL) return cached as T | null;

    // 2. Request já em andamento → reutiliza a mesma promise
    const inflight = this.inflightCache.get(key);
    if (inflight) return inflight as Promise<T | null>;

    // 3. Novo request
    const promise = fetcher()
      .then((value) => {
        this.set(key, value, ttlMs); // grava mesmo se value === null
        return value;
      })
      .finally(() => {
        this.inflightCache.delete(key);
      });

    this.inflightCache.set(key, promise);
    return promise;
  }

  // ─── Leitura ─────────────────────────────────────────────────────────────

  /**
   * Retorna o valor cacheado, ou null se não existir / expirado.
   * Não distingue "ausente" de "valor null" — use getRaw para isso.
   */
  static get<T>(key: string): T | null {
    const raw = this.getRaw(key);
    return raw === CACHE_NULL ? null : (raw as T | null);
  }

  /**
   * Retorna:
   *  - O valor (inclusive null) se a chave existir e não tiver expirado
   *  - Symbol CACHE_NULL se a chave não existir ou tiver expirado
   */
  private static getRaw(key: string): any {
    // Memória
    const mem = this.memoryCache.get(key);
    if (mem) {
      if (this.isValid(mem)) return mem.value; // pode ser null
      this.memoryCache.delete(key);
    }

    // sessionStorage
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null) return CACHE_NULL;

      const entry: CacheEntry<any> = JSON.parse(raw);
      if (!this.isValid(entry)) {
        sessionStorage.removeItem(key);
        return CACHE_NULL;
      }

      // null serializa/desserializa corretamente via JSON
      const value = entry.value === null ? null : entry.value;
      this.memoryCache.set(key, { ...entry, value }); // promove para memória
      return value;
    } catch {
      return CACHE_NULL;
    }
  }

  // ─── Escrita ──────────────────────────────────────────────────────────────

  static set<T>(key: string, value: T | null, ttlMs?: number): void {
    const entry: CacheEntry<T | null> = {
      value,
      expiresAt: ttlMs ? Date.now() + ttlMs : null,
    };

    this.memoryCache.set(key, entry);

    try {
      sessionStorage.setItem(key, JSON.stringify(entry));
    } catch (e) {
      console.warn("[CacheService] sessionStorage quota atingida:", e);
    }
  }

  // ─── Invalidação ─────────────────────────────────────────────────────────

  static invalidate(key: string): void {
    this.memoryCache.delete(key);
    this.inflightCache.delete(key);
    try { sessionStorage.removeItem(key); } catch { /* noop */ }
  }

  static invalidateByPrefix(prefix: string): void {
    for (const key of Array.from(this.memoryCache.keys())) {
      if (key.startsWith(prefix)) this.memoryCache.delete(key);
    }
    for (const key of Array.from(this.inflightCache.keys())) {
      if (key.startsWith(prefix)) this.inflightCache.delete(key);
    }
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k?.startsWith(prefix)) toRemove.push(k);
      }
      toRemove.forEach(k => sessionStorage.removeItem(k));
    } catch { /* noop */ }
  }

  static clear(): void {
    this.memoryCache.clear();
    this.inflightCache.clear();
    try { sessionStorage.clear(); } catch { /* noop */ }
  }

  // ─── Utilidade ───────────────────────────────────────────────────────────

  private static isValid<T>(entry: CacheEntry<T>): boolean {
    return entry.expiresAt === null || Date.now() < entry.expiresAt;
  }

  static has(key: string): boolean {
    return this.getRaw(key) !== CACHE_NULL;
  }
}

export default CacheService;